/**
 * Carries conversion events across a navigation.
 *
 * `window.dataLayer` is a plain array until GTM initialises, and GTM replays what it finds
 * there — but the array dies with the document. An event pushed immediately before a redirect
 * is therefore lost whenever the container has not loaded yet, which under our deferral is
 * most of the time. Staging the payload here and emitting it on the next page instead moves
 * the push into a page with no pending navigation, where the replay guarantee holds.
 */

const STORAGE_KEY = 'pendingConversions';

/** Beyond this, a staged event is assumed abandoned rather than in-flight. */
const MAX_AGE_MS = 30 * 60 * 1000;

/** Bounds a corrupted or runaway queue; one journey stages at most a handful. */
const MAX_QUEUED = 10;

type StagedConversion = {
  event: Record<string, unknown>;
  stagedAt: number;
};

function pushToDataLayer(payload: Record<string, unknown>): void {
  const w = window as Window & { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push(payload);
}

function readQueue(): StagedConversion[] {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return [];
  }
  if (raw === null) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as StagedConversion[]) : [];
  } catch {
    return [];
  }
}

/**
 * Gets a conversion delivered across an imminent navigation.
 *
 * Normally stages it for the next page. When storage is unavailable (private mode, blocked
 * site data) it falls back to pushing immediately — which is exactly today's behaviour, so
 * the worst case is no worse than before rather than a hard failure.
 *
 * A queue rather than a single slot: one journey can stage more than one conversion (the
 * brochure request and the newsletter sign-up), and a single slot would silently drop one.
 */
export function deliverConversion(payload: Record<string, unknown>): void {
  const staged: StagedConversion = { event: payload, stagedAt: Date.now() };
  const queue = readQueue();
  queue.push(staged);

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(queue.slice(-MAX_QUEUED)));
  } catch {
    pushToDataLayer(payload);
  }
}

/** Emits every staged event, each exactly once. Safe to call on any page. */
export function consumePendingConversion(): void {
  const queue = readQueue();

  // Cleared before the pushes, never after: a reload of the thank-you page must not fire a
  // second time, and a throw mid-push must not leave the queue re-armed.
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    return;
  }

  const now = Date.now();
  queue.forEach((staged) => {
    if (!staged || typeof staged.stagedAt !== 'number' || !staged.event) {
      return;
    }
    if (now - staged.stagedAt > MAX_AGE_MS) {
      return;
    }
    pushToDataLayer(staged.event);
  });
}
